import React, { useState, useContext, useEffect, useRef } from "react";
import { AccountContext } from "../../store/AccountContext";
import { Countdown } from "../common/Countdown";

import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  inputBaseClasses,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import CardPaymentForm from "./CardPaymentForm";
import usePromise from "react-use-promise";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from "@mui/icons-material/Error";
import { useNavigate, useLocation } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import { CurrencySymbol } from "@booyaltd/core";
import querystring from "query-string";
import dayjs from "dayjs";

/**
 * PM5 T2 (money path, batch "payment") — converted from MUI v4 + `makeStyles`
 * to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * PAYMENTS + SUBSCRIPTIONS ESCALATION CLASS — this is the two-step membership
 * checkout: step 1 picks the product (and the currency, which decides which
 * Stripe prices are offered), step 2 hosts `CardPaymentForm` and renders the
 * payment-response states (pending / failure / timeout / success). Conversion
 * is styling-only: not one handler, condition, state value, `useEffect`
 * dependency array, redirect target or piece of copy moved. The `#691`
 * once-only auto-renew redirect ref, the `qs.app` deep-link back to
 * `farmflix://`, and the product/currency filters are byte-for-byte unchanged.
 *
 * `useStyles(props)` took its values off `props.dark` only (verified key by
 * key), so every ported rule that varied is a `(dark) => (theme) => ({…})`
 * factory reading the same boolean.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1) — declared in `useStyles` but never
 * referenced from this file's JSX, so they styled nothing: `subscriptionType`,
 * `responseTitle` (the response heading actually uses `stepTitle`), and
 * `center`. `bestValue` is referenced only from the two commented-out
 * "SAVE 20%/30%" promo blocks below; it is dropped for the same reason (an
 * unused const would also fail lint). If those blocks are ever re-enabled the
 * badge style has to come back with them.
 */

/* ---------------------------------------------------------------------- *
 * styled() — bare DOM elements. None of these accept an `sx` prop, so
 * `styled()` is the port (guide §1.2). `dark` is a style-only prop and is
 * filtered out of the DOM.
 * ---------------------------------------------------------------------- */

const darkOnly = { shouldForwardProp: (prop) => prop !== "dark" };

// was: useStyles().background — the page's outermost <div>.
const BackgroundDiv = styled(
  "div",
  darkOnly,
)(({ dark }) => ({
  backgroundColor: dark ? "transparent" : "#e8e8e8",
  display: "flex",
  textAlign: "center",
}));

// was: useStyles().orContent — the "OR" divider label <span>.
const OrContentSpan = styled("span")(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  fontWeight: 500,
  fontSize: "0.9rem",
  color: theme.palette.background.ffNavy,
}));

// was: useStyles().subscriptionCost
const SubscriptionCostSpan = styled("span")(({ theme }) => ({
  fontSize: "1.6rem",
  lineHeight: "1.8rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.4rem",
    lineHeight: "3.6rem",
  },
}));

// was: useStyles().subscriptionTerm
const SubscriptionTermSpan = styled("span")({
  fontWeight: 500,
  fontSize: "0.9rem",
});

// was: useStyles().discountedSubscriptionOriginalCost
const DiscountedOriginalCostSpan = styled("span")(({ theme }) => ({
  textDecoration: "line-through",
  color: "#888",
  fontSize: "1.6rem",
  lineHeight: "1.8rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.4rem",
    lineHeight: "3.6rem",
  },
}));

// was: useStyles().discountedSubscriptionNewCost
const DiscountedNewCostSpan = styled(
  "span",
  darkOnly,
)(({ theme, dark }) => ({
  marginLeft: "6px",
  color: dark ? "#eee" : theme.palette.background.upgrade,
  fontSize: "1.6rem",
  lineHeight: "1.8rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.4rem",
    lineHeight: "3.6rem",
  },
}));

// was: useStyles().discountedSubscriptionTerm
const DiscountedTermSpan = styled(
  "span",
  darkOnly,
)(({ theme, dark }) => ({
  fontWeight: 500,
  fontSize: "0.9rem",
  color: dark ? "#eee" : theme.palette.background.upgrade,
}));

// was: useStyles().selectedMembershipPrice
const SelectedMembershipPriceSpan = styled(
  "span",
  darkOnly,
)(({ theme, dark }) => ({
  color: dark ? "#eee" : theme.palette.background.ffNavy,
  fontWeight: 400,
  fontSize: "1rem",
}));

// was: useStyles().selectedMembershipTerm
const SelectedMembershipTermSpan = styled(
  "span",
  darkOnly,
)(({ theme, dark }) => ({
  color: dark ? "#eee" : theme.palette.background.ffNavy,
  fontWeight: 400,
  fontSize: "0.8rem",
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ Guide §2, "`<Grid container>` lost its `width: 100%`". v4's
 * `.MuiGrid-container` declared `width: 100%` unconditionally; v9's
 * `generateGridStyles` emits no width rule at all, so a container nested
 * directly inside another container shrink-to-fits. Restated verbatim on every
 * converted container (a no-op on the block-level ones).
 */
const v4GridContainerWidthSx = { width: "100%" };

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim: omit `spacing` (v9 default
 * 0 ⇒ `gap: 0px`) and give the item the 8px padding itself. This container's
 * own `width` supersedes `v4GridContainerWidthSx`, exactly as in v4.
 */
const v4SpacingContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

/**
 * ♦ Guide §2, "Grid without a container". Several `<Grid item>`s here sit
 * inside a plain `<div>`/fragment or inside another Grid ITEM, so they have no
 * Grid CONTAINER as an ancestor. v9 publishes `--Grid-parent-columns` only from
 * a container's `> *` rule, so a `size` `calc()` would be invalid and the
 * element would fall back to full width by accident rather than by rule.
 * Ported as `Box`es carrying the literal v4 declarations. Every orphan in this
 * file is 12/12 columns, i.e. 100% at every breakpoint, so one unconditional
 * set reproduces them all. (`.MuiGrid-item`'s `margin: 0` is a div's initial
 * value, so it is not restated — same as `Footer.js`.)
 */
const orphanGridItemFullSx = {
  boxSizing: "border-box",
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

// was: useStyles().stepWrapper. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted queries are identical.
const stepWrapperSx = (theme) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(6),
  },
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
});

// was: useStyles().stepTitle
const stepTitleSx = (dark) => (theme) => ({
  color: dark
    ? theme.palette.common.white
    : theme.palette.background.ffNavyLighter,
  fontSize: "1.5rem",
  textAlign: "left",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2rem",
    lineHeight: "2.2rem",
  },
});

// was: useStyles().stepSubTitle
const stepSubTitleSx = (dark) => (theme) => ({
  marginBottom: theme.spacing(2),
  color: dark ? "#d9d9d9" : theme.palette.background.ffNavyLighter,
  textAlign: "left",
});

// was: useStyles().stepText
const stepTextSx = (dark) => (theme) => ({
  marginBottom: theme.spacing(2),
  color: dark ? "#fff" : theme.palette.background.ffNavyLighter,
  textAlign: "left",
});

// was: useStyles().stepTextBack
const stepTextBackSx = (dark) => (theme) => ({
  marginBottom: theme.spacing(2),
  color: dark ? "#fff" : theme.palette.background.ffNavyLighter,
  textAlign: "right",
  textDecoration: "underline",
  "&:hover": {
    cursor: "pointer",
  },
});

/**
 * was: useStyles().currencySelector.
 *
 * Two things needed here beyond the mechanical port:
 *
 * 1. §1.2b — the v4 rule reached into the child this component renders with a
 *    hard-coded global `& .MuiInputBase-input`. Under the `FfV5` prefix that
 *    string matches nothing, so the name is read off `inputBaseClasses`
 *    instead (safe here regardless of the static-`*Classes` bake-order trap in
 *    guide §2: the app entry imports `classNamePrefix` first). It still beats
 *    `themeV5`'s own `MuiInputBase.input` border, which is what it existed to
 *    override — the selector is (0,2,0) against the theme's (0,1,0), exactly
 *    as in v4.
 * 2. `variant="standard"` is pinned on the `<Select>` itself (guide §2,
 *    "`Select` default `variant`") — v9 defaults to `outlined`, which would
 *    render a different control with a notched border and take it out of range
 *    of `themeV5`'s `MuiInput.underline` overrides.
 */
const currencySelectorSx = (dark) => (theme) => ({
  backgroundColor: dark ? "#525252" : "#dadada",
  color: dark ? "#fff" : "#000",
  textAlign: "left",
  marginBottom: theme.spacing(1),
  outline: "none",
  fontWeight: 500,
  [`& .${inputBaseClasses.input}`]: {
    border: 0,
  },
});

// was: useStyles().subscriptionContainer. Layered AFTER `orphanGridItemFullSx`
// at every call site, because in v4 the Grid classes came from MUI's own JSS
// sheet and this file's class from a later one — so `flexBasis: "unset"` and
// `width: "100%"` beat the Grid item's `flex-basis: 100%`, as they did before.
const subscriptionContainerSx = (dark) => (theme) => ({
  color: dark ? "#fff" : theme.palette.background.ffNavy,
  backgroundColor: dark ? "#3a3a3a" : "#dadada",
  padding: theme.spacing(2),
  display: "inline-block",
  textAlign: "left",
  width: "100%",
  flexBasis: "unset",
  margin: theme.spacing(1, 0),
  "&:hover": {
    backgroundColor: dark ? "#484848" : "#e2e2e2",
    cursor: "pointer",
  },
});

// was: useStyles().selectedSubscriptionContainer
const selectedSubscriptionContainerSx = (dark) => (theme) => ({
  color: theme.palette.common.white,
  backgroundColor: dark ? "#666666" : "#f9f9f9",
  padding: theme.spacing(2),
  display: "inline-block",
  width: "100%",
  textAlign: "left",
  margin: theme.spacing(1, 0),
  "&:hover": {
    backgroundColor: dark ? "#666666" : "#f9f9f9",
    cursor: "pointer",
  },
});

// was: useStyles().selectButton. Both branches of the v4 `border` /
// `backgroundColor` ternaries resolved to the same value; kept as written so
// the port stays diff-able against the v4 rule.
const selectButtonSx = (dark) => (theme) => ({
  backgroundColor: dark
    ? theme.palette.background.ffNavyLighter
    : theme.palette.background.ffNavyLighter,
  border: "2px solid #292929",
  color: theme.palette.common.white,
  padding: "8px 14px",
  fontSize: "0.8rem",
  lineHeight: "1.2rem",
  marginRight: 0,
  "&:hover": {
    border: dark ? "2px solid #292929" : "2px solid #171717",
    backgroundColor: dark
      ? theme.palette.background.ffNavyLighter
      : theme.palette.background.ffNavyLighter,
    color: theme.palette.common.white,
  },
  "& svg": {
    fill: theme.palette.background.ffGreen,
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.9rem",
  },
});

// was: useStyles().selectButtonUnchecked
const selectButtonUncheckedSx = (dark) => (theme) => ({
  transition: "none",
  backgroundColor: dark ? "transparent" : "#ddd",
  border: dark ? "2px solid #eee" : "2px solid #171717",
  color: dark ? "#eee" : "#171717",
  padding: "8px 14px",
  fontSize: "0.8rem",
  lineHeight: "1.2rem",
  marginRight: 0,
  "&:hover": {
    border: dark ? "2px solid #eee" : "2px solid #171717",
    backgroundColor: dark ? "transparent" : "#ddd",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.9rem",
  },
});

// was: useStyles().subscriptionPrice
const subscriptionPriceSx = (dark) => (theme) => ({
  letterSpacing: "-1px",
  fontWeight: 600,
  color: dark ? "#eee" : theme.palette.background.ffNavyLighter,
  marginTop: theme.spacing(1),
});

// was: useStyles().subscriptionName
const subscriptionNameSx = (dark) => (theme) => ({
  color: dark
    ? theme.palette.background.ffGreen
    : theme.palette.background.ffNavyLighter,
  fontSize: "1.4rem",
  lineHeight: "1.6rem",
  fontWeight: 500,
  textAlign: "left",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2rem",
    lineHeight: "2.2rem",
  },
});

// was: useStyles().subscriptionDescription
const subscriptionDescriptionSx = (dark) => (theme) => ({
  color: dark ? "#eee" : theme.palette.background.upgradeAlt,
  fontSize: "0.9rem",
  lineHeight: "1rem",
  fontWeight: "400",
  textAlign: "left",
  margin: theme.spacing(0.5, 0, 1, 0),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1rem",
    lineHeight: "1.1rem",
    margin: theme.spacing(0.5, 0, 1, 0),
  },
});

// was: useStyles().infoText
const infoTextSx = (dark) => (theme) => ({
  color: dark ? "#eee" : theme.palette.background.upgradeAlt,
  textAlign: "left",
  fontSize: "0.8rem",
  lineHeight: "0.9rem",
  marginTop: theme.spacing(1),
  "& span": {
    fontWeight: 500,
  },
});

/**
 * was: useStyles().continueButton, plus a v4 default that has to be restored.
 *
 * PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
 * NOT in the v4 rule. v4's plain `<Button>` defaulted to `color="default"`,
 * whose root style was `color: theme.palette.text.primary`. v5+ deleted
 * `default` and made `primary` the default — and this theme's `primary.main`
 * IS the lime that `typography.button` paints the button background with, so an
 * uncompensated port renders an invisible lime-on-lime "Continue" button.
 */
const continueButtonSx = (theme) => ({
  marginTop: theme.spacing(3),
  width: "100%",
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    width: "auto",
    padding: theme.spacing(1.5, 5),
  },
  color: "text.primary",
});

// PARITY RESTORE, same row: the two response-screen Buttons ("Try Again",
// "Get Started") carried no v4 class at all, so the removed `color="default"`
// is the only thing painting their labels.
const defaultButtonColourSx = { color: "text.primary" };

// was: useStyles().smlDivider
const smlDividerSx = (theme) => ({
  backgroundColor: "#dadada",
  height: "2px",
  width: "50%",
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(1.5),
});

// was: useStyles().selectedMembershipContainer
const selectedMembershipContainerSx = (theme) => ({
  marginBottom: theme.spacing(4),
});

// was: useStyles().selectedMembership
const selectedMembershipSx = (dark) => (theme) => ({
  zIndex: "-100000",
  backgroundColor: dark ? "#666666" : theme.palette.common.white,
  padding: theme.spacing(3, 2),
});

// was: useStyles().selectedMembershipTitle
const selectedMembershipTitleSx = (dark) => (theme) => ({
  textAlign: "left",
  color: dark
    ? theme.palette.background.ffGreen
    : theme.palette.background.ffNavy,
  fontWeight: 500,
  fontSize: "1.6rem",
});

// was: useStyles().selectedMembershipLabel
const selectedMembershipLabelSx = (dark) => (theme) => ({
  color: dark ? "#eee" : theme.palette.background.ffNavy,
  textAlign: "left",
  fontSize: "1.2rem",
  lineHeight: "1.6rem",
  marginTop: "20px",
  marginBottom: "4px",
  fontWeight: 500,
  fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
});

// was: useStyles().selectedMembershipBodyText
const selectedMembershipBodyTextSx = (dark) => (theme) => ({
  color: dark ? "#d9d9d9" : theme.palette.background.upgradeAlt,
  marginBottom: theme.spacing(1.5),
  fontSize: "0.9rem",
  lineHeight: "1rem",
  textAlign: "left",
});

// was: useStyles().responseBox
const responseBoxSx = {
  textAlign: "center",
  padding: "10%",
};

// was: useStyles().responseIcon
const responseIconSx = (theme) => ({
  width: "4rem",
  height: "4rem",
  [theme.breakpoints.up("sm")]: {
    width: "5rem",
    height: "5rem",
  },
});

// was: useStyles().divider
const dividerSx = (theme) => ({
  width: "50%",
  margin: "10% 25% 10% 25%",
  height: "1px",
  backgroundColor: "#a5a5a5",
  [theme.breakpoints.up("sm")]: {
    margin: "5% 25% 10px 25%",
  },
  [theme.breakpoints.up("md")]: {
    margin: "5% 25% 10px 25%",
  },
});

// was: useStyles().responseSubTitle
const responseSubTitleSx = (dark) => (theme) => ({
  color: dark
    ? theme.palette.common.upgrade
    : theme.palette.background.ffNavyLighter,
  fontSize: "1.2rem",
  textAlign: "center",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(3),
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().responseBodytext
const responseBodytextSx = (dark) => (theme) => ({
  marginBottom: theme.spacing(2),
  color: dark ? theme.palette.common.upgrade : theme.palette.background.ffNavy,
  textAlign: "left",
});

// was: useStyles().responseHelp
const responseHelpSx = (dark) => (theme) => ({
  color: dark ? theme.palette.common.upgrade : theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  lineHeight: "1.1rem",
  textAlign: "center",
  "& a": {
    color: dark ? theme.palette.common.white : theme.palette.background.ffNavy,
  },
});

// was: useStyles().orContainer
const orContainerSx = {
  display: "flex",
  alignItems: "center",
};

// was: useStyles().orBorder
const orBorderSx = {
  borderBottom: "1px solid #c1c1c1",
  width: "100%",
};

// was: useStyles().changeButton. This one DOES set `color`, so the removed
// v4 `color="default"` default has nothing left to restore.
const changeButtonSx = (dark) => (theme) => ({
  color: dark ? "#d9d9d9" : theme.palette.background.ffNavy,
  border: dark ? "2px solid #d9d9d9" : "2px solid #171717",
  "&:hover": {
    color: dark ? "#d9d9d9" : theme.palette.background.ffNavy,
    border: dark ? "2px solid #d9d9d9" : "2px solid #171717",
  },
});

// was: useStyles().loadingBox
const loadingBoxSx = (theme) => ({
  marginTop: theme.spacing(2),
});

// was: useStyles().loadingText
const loadingTextSx = (dark) => (theme) => ({
  marginBottom: theme.spacing(2),
  color: dark ? theme.palette.common.upgrade : theme.palette.background.ffNavy,
  textAlign: "center",
  marginTop: theme.spacing(2),
});

// was: useStyles().discountCountdownLabel
const discountCountdownLabelSx = (dark) => (theme) => ({
  color: dark ? "#fff" : "#555",
  fontWeight: 500,
  marginRight: "4px",
  fontSize: "1rem",
  lineHeight: "1.1rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.1rem",
    lineHeight: "1.2rem",
  },
});

// was: useStyles().discountCountdownText
const discountCountdownTextSx = (theme) => ({
  color: "red",
  fontWeight: 700,
  fontSize: "1rem",
  lineHeight: "1.2rem",
  marginTop: theme.spacing(0.5),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.1rem",
    lineHeight: "1.2rem",
  },
});

const Steps = (props) => {
  const {
    getSubscriptionProducts,
    subscriptionProducts,
    resetErrors,
    userProfile,
    getMembershipDetails,
  } = useContext(AccountContext);

  // Destructured once here because `ResponseBox` below shadows `props` with its
  // own parameter — reading `props.dark` inside it would read the wrong object.
  const { dark } = props;
  const navigate = useNavigate();
  const { search } = useLocation();
  const [qs, setQs] = useState({ app: false, product: undefined });

  //Redirect Standard Users and Standard Expired w/ Auto Renew Enabled from this page
  // Navigating is a side effect — render-body calls double-fire under
  // StrictMode's dev double-render; the ref keeps the push once-only (#691,
  // same pattern as LoggedInLayout). As before the fix, the page still
  // renders beneath the redirect for the frame it takes to leave.
  const autoRenewRedirectTarget = !userProfile
    ? undefined
    : userProfile.memberStatus === "standard" && userProfile.productAutoRenewing
      ? "/videos"
      : userProfile.memberStatus === "standard-expired" &&
          userProfile.productAutoRenewing
        ? "/myaccount/overview"
        : undefined;
  const autoRenewRedirected = useRef(false);
  useEffect(() => {
    if (autoRenewRedirectTarget && !autoRenewRedirected.current) {
      autoRenewRedirected.current = true;
      navigate(autoRenewRedirectTarget);
    }
  }, [autoRenewRedirectTarget, navigate]);

  useEffect(() => {
    getSubscriptionProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSubscriptionProducts =
    subscriptionProducts &&
    subscriptionProducts?.results.find(
      (p) => p.memberStatusId === "standard" && p.isSubscription,
    );

  const isPPVProducts =
    subscriptionProducts &&
    subscriptionProducts?.results.find(
      (p) => p.memberStatusId === "standard" && !p.isSubscription,
    );

  // Subscription State Management
  const [selectedSubscription, setSelectedSubscription] = useState("");
  const [selectedSubscriptionID, setSelectedSubscriptionID] = useState("");
  // eslint-disable-next-line
  const [currency, setCurrency] = useState();
  // eslint-disable-next-line
  const [reloadTrigger, setReloadTrigger] = useState(Date.now());

  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);

  // eslint-disable-next-line
  const loading = state === "pending";

  const handleMembershipChange = () => {
    setActiveStep(0);
  };

  useEffect(() => {
    if (membershipDetails && membershipDetails.currency) {
      setCurrency(membershipDetails.currency.toUpperCase());
    } else {
      setCurrency("GBP");
    }
  }, [membershipDetails]);

  /* ==============================================
  =========== Subscription Handlers ===============
  ============================================== */

  // Handle Currenct Change
  const handleCurrencyChange = (event) => {
    setSelectedSubscription("");
    setSelectedSubscriptionID("");
    setCurrency(event.target.value);
  };

  function onHandleSubscription(subscription) {
    setSelectedSubscription(subscription);
    setSelectedSubscriptionID(subscription.id);
  }

  /* ==============================================
  ============= Payment Handlers ==================
  ============================================== */

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    resetErrors();
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return <Step1Subscriptions />;
      case 1:
        return <Step2Payment />;
      default:
        return "Unknown step";
    }
  }

  // Step 1 - Show Membership Plans
  function Step1Subscriptions() {
    return (
      <>
        {membershipDetails && (
          <>
            <Container
              fixed
              fixedcomponent="main"
              maxWidth="sm"
              style={{ padding: "0" }}
            >
              <Grid container sx={v4GridContainerWidthSx}>
                <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={stepWrapperSx}>
                  {props.showStepper !== false && (
                    <Typography variant="body2" sx={stepTextSx(dark)}>
                      Step <b>1</b> of <b>2</b>
                    </Typography>
                  )}

                  <Typography variant="h2" sx={stepTitleSx(dark)}>
                    Choose your Membership
                  </Typography>
                  <Grid container sx={v4GridContainerWidthSx}>
                    <Grid size={{ xs: 8 }}>
                      <Typography variant="body1" sx={stepSubTitleSx(dark)}>
                        Select a FarmFLiX Membership.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }} align="right">
                      {!membershipDetails.stripeCustomerId && (
                        <Select
                          id="currency"
                          name="currency"
                          variant="standard"
                          value={currency}
                          onChange={handleCurrencyChange}
                          sx={currencySelectorSx(dark)}
                        >
                          <MenuItem value={"GBP"}>{`GBP (£)`}</MenuItem>
                          <MenuItem value={"EUR"}>{`EUR (€)`}</MenuItem>
                          <MenuItem value={"USD"}>{`USD ($)`}</MenuItem>
                        </Select>
                      )}
                    </Grid>
                  </Grid>
                  {subscriptionProducts && (
                    <div>
                      <>
                        {isPPVProducts && (
                          <>
                            {subscriptionProducts.results
                              .filter(
                                (s) =>
                                  s.currency === currency &&
                                  s.cost > 0 &&
                                  !s.isSubscription,
                              )
                              .map((subscription, index) => (
                                <Box
                                  key={index}
                                  sx={[
                                    orphanGridItemFullSx,
                                    subscription.id === selectedSubscriptionID
                                      ? selectedSubscriptionContainerSx(dark)
                                      : subscriptionContainerSx(dark),
                                  ]}
                                  onClick={() =>
                                    onHandleSubscription(subscription)
                                  }
                                >
                                  <Grid container sx={v4GridContainerWidthSx}>
                                    <Grid size={{ xs: 7 }}>
                                      <Box style={{ display: "flex" }}>
                                        <Typography
                                          sx={subscriptionNameSx(dark)}
                                        >
                                          {subscription.name}
                                        </Typography>

                                        {/* {(subscription.id === "annual60" ||
                                          subscription.id === "annual70eur" ||
                                          subscription.id ===
                                            "annual85usd") && (
                                          <Typography
                                            className={classes.bestValue}
                                          >
                                            SAVE 20%
                                          </Typography>
                                        )} */}
                                      </Box>
                                      <Typography
                                        sx={subscriptionDescriptionSx(dark)}
                                      >
                                        {subscription.description}
                                      </Typography>

                                      {/* Price */}
                                      <Typography
                                        sx={subscriptionPriceSx(dark)}
                                        variant="body1"
                                      >
                                        <SubscriptionCostSpan>
                                          {CurrencySymbol({
                                            currency: subscription.currency,
                                          })}
                                          {subscription.cost}
                                        </SubscriptionCostSpan>
                                      </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 5 }} align="right">
                                      {subscription.id ===
                                      selectedSubscriptionID ? (
                                        <Button
                                          name={subscription.id}
                                          disableRipple={true}
                                          sx={selectButtonSx(dark)}
                                          startIcon={<CheckCircleIcon />}
                                        >
                                          SELECTED
                                        </Button>
                                      ) : (
                                        <Button
                                          name={subscription.id}
                                          disableRipple={true}
                                          disableFocusRipple={true}
                                          sx={selectButtonUncheckedSx(dark)}
                                          startIcon={
                                            <RadioButtonUncheckedOutlinedIcon />
                                          }
                                        >
                                          SELECT
                                        </Button>
                                      )}
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                          </>
                        )}
                        {isSubscriptionProducts && isPPVProducts && (
                          <Box sx={orContainerSx}>
                            <Box sx={orBorderSx} />
                            <OrContentSpan>OR</OrContentSpan>
                            <Box sx={orBorderSx} />
                          </Box>
                        )}
                        {isSubscriptionProducts && (
                          <>
                            {subscriptionProducts.results
                              .filter(
                                (s) =>
                                  s.currency === currency &&
                                  s.cost > 0 &&
                                  s.isSubscription,
                              )
                              .map((subscription, index) => (
                                <Box
                                  key={index}
                                  sx={[
                                    orphanGridItemFullSx,
                                    subscription.id === selectedSubscriptionID
                                      ? selectedSubscriptionContainerSx(dark)
                                      : subscriptionContainerSx(dark),
                                  ]}
                                  onClick={() =>
                                    onHandleSubscription(subscription)
                                  }
                                >
                                  <Grid container sx={v4GridContainerWidthSx}>
                                    <Grid size={{ xs: 7 }}>
                                      <Box style={{ display: "flex" }}>
                                        <Typography
                                          sx={subscriptionNameSx(dark)}
                                        >
                                          {subscription.name}
                                        </Typography>

                                        {/* {(subscription.id === "annual60" ||
                                          subscription.id === "annual70eur" ||
                                          subscription.id ===
                                            "annual85usd") && (
                                          <Typography
                                            className={classes.bestValue}
                                          >
                                            SAVE 30%
                                          </Typography>
                                        )} */}
                                      </Box>
                                      <Typography
                                        sx={subscriptionDescriptionSx(dark)}
                                      >
                                        {subscription.description}
                                      </Typography>

                                      {/* Price */}
                                      <Typography
                                        sx={subscriptionPriceSx(dark)}
                                        variant="body1"
                                      >
                                        {subscription.discounted && (
                                          <>
                                            <DiscountedOriginalCostSpan>
                                              {CurrencySymbol({
                                                currency: subscription.currency,
                                              })}
                                              {parseFloat(
                                                subscription.discountedOrigCost,
                                              ).toFixed(2)}
                                            </DiscountedOriginalCostSpan>
                                            <DiscountedNewCostSpan dark={dark}>
                                              {CurrencySymbol({
                                                currency: subscription.currency,
                                              })}
                                              {subscription.cost}
                                            </DiscountedNewCostSpan>
                                            <DiscountedTermSpan dark={dark}>
                                              /
                                              {
                                                subscription.entitlementPeriodUnit
                                              }
                                            </DiscountedTermSpan>
                                          </>
                                        )}
                                        {!subscription.discounted && (
                                          <>
                                            <SubscriptionCostSpan>
                                              {CurrencySymbol({
                                                currency: subscription.currency,
                                              })}
                                              {subscription.cost}
                                            </SubscriptionCostSpan>
                                            <SubscriptionTermSpan>
                                              /
                                              {
                                                subscription.entitlementPeriodUnit
                                              }
                                            </SubscriptionTermSpan>
                                          </>
                                        )}
                                      </Typography>
                                      {subscription.discounted &&
                                        subscription.availableUntil && (
                                          /* Guide §2, "`makeStyles`
                                             `className` alongside `Box` system
                                             props": the ex-system-prop goes
                                             FIRST and the ex-`className` rules
                                             after it, which is the order v4
                                             resolved them in. */
                                          <Box
                                            sx={[
                                              { display: "flex" },
                                              discountCountdownTextSx,
                                            ]}
                                          >
                                            <Typography
                                              sx={discountCountdownLabelSx(
                                                dark,
                                              )}
                                            >
                                              Ends in:
                                            </Typography>
                                            <Countdown
                                              date={dayjs(
                                                subscription.availableUntil,
                                              ).toDate()}
                                              zeroPadTime={2}
                                              daysInHours={true}
                                            />
                                          </Box>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 5 }} align="right">
                                      {subscription.id ===
                                      selectedSubscriptionID ? (
                                        <Button
                                          name={subscription.id}
                                          disableRipple={true}
                                          sx={selectButtonSx(dark)}
                                          startIcon={<CheckCircleIcon />}
                                        >
                                          SELECTED
                                        </Button>
                                      ) : (
                                        <Button
                                          name={subscription.id}
                                          disableRipple={true}
                                          disableFocusRipple={true}
                                          sx={selectButtonUncheckedSx(dark)}
                                          startIcon={
                                            <RadioButtonUncheckedOutlinedIcon />
                                          }
                                        >
                                          SELECT
                                        </Button>
                                      )}
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                          </>
                        )}
                      </>
                      <Box sx={orphanGridItemFullSx}>
                        <Typography variant="body2" sx={infoTextSx(dark)}>
                          All Memberships offer unlimited access to FarmFLiX,
                          and up to 3 devices at concurrent times. HD (720p),
                          Full HD (1080p) availability subject to your internet
                          service and device capability.
                        </Typography>
                      </Box>
                      <Box sx={orphanGridItemFullSx}>
                        {activeStep === 0 && (
                          <Button
                            name="continue"
                            size="large"
                            fullwidth="true"
                            onClick={handleNext}
                            sx={continueButtonSx}
                            disabled={!selectedSubscriptionID}
                          >
                            Continue
                          </Button>
                        )}
                      </Box>
                      <Box sx={orphanGridItemFullSx}>
                        <Divider sx={smlDividerSx} />
                        <Typography variant="body2" sx={infoTextSx(dark)}>
                          <span>We offer the FarmFLiX Guarantee</span>. If you
                          don&apos;t love FarmFLiX in the first month,
                          we&apos;ll refund your order, no questions asked.
                        </Typography>
                      </Box>
                    </div>
                  )}
                </Grid>
              </Grid>
            </Container>
          </>
        )}
      </>
    );
  }

  function Step2Payment() {
    const [paymentResponse, setPaymentResponse] = useState();

    const tryAgain = () => {
      setPaymentResponse("");
      setActiveStep(0);
    };

    const ResponseBox = (props) => {
      if (!paymentResponse || paymentResponse === undefined) {
        console.log("Should be no status set", paymentResponse);
        return;
      }

      return (
        <>
          <Box sx={responseBoxSx}>
            {props.paymentStatus === "pending" && (
              <CircularProgress color="primary" />
            )}
            {props.paymentStatus === "failure" && (
              <CancelIcon sx={responseIconSx} color="error" />
            )}
            {props.paymentStatus === "timeout" && (
              <ErrorIcon sx={responseIconSx} style={{ fill: "#ff9f00" }} />
            )}
            {props.paymentStatus === "success" && (
              <CheckCircleIcon sx={responseIconSx} color="primary" />
            )}
            <Typography
              variant="h2"
              sx={stepTitleSx(dark)}
              style={{ textAlign: "center" }}
            >
              {props.paymentStatusTitle}
            </Typography>
            <Typography
              variant="h3"
              sx={responseSubTitleSx(dark)}
              style={{ textAlign: "center" }}
            >
              {props.paymentStatusSubTitle}
            </Typography>
            {props.paymentStatusDesc && (
              <Typography
                variant="body2"
                sx={responseBodytextSx(dark)}
                style={{ textAlign: "center" }}
              >
                {props.paymentStatusDesc}
              </Typography>
            )}
            {props.tryAgain && (
              <>
                <Button
                  size="large"
                  onClick={tryAgain}
                  sx={defaultButtonColourSx}
                >
                  Try Again
                </Button>
                <Divider sx={dividerSx} />
                <Typography variant="body2" sx={responseHelpSx(dark)}>
                  <b>Need help?</b> Contact us using the &apos;Help&apos; Widget
                  or email us at{" "}
                  <a href="mailto:info@farmflix.tv">info.farmflix.tv</a>
                </Typography>
              </>
            )}
            {props.paymentStatus === "success" && (
              <a
                href={qs.app ? "farmflix://" : "/videos"}
                style={{ textDecoration: "none" }}
              >
                <Button
                  size="large"
                  name="get-started"
                  sx={defaultButtonColourSx}
                >
                  Get Started
                </Button>
              </a>
            )}
          </Box>
        </>
      );
    };

    return (
      <>
        {paymentResponse && (
          <ResponseBox
            paymentStatus={paymentResponse.status}
            paymentStatusTitle={paymentResponse.title}
            paymentStatusSubTitle={paymentResponse.subTitle}
            paymentStatusDesc={paymentResponse.description}
            tryAgain={paymentResponse.tryAgain}
          />
        )}
        <Container
          fixed
          fixedcomponent="main"
          maxWidth="sm"
          style={{
            padding: 0,
            display: paymentResponse ? "none" : "block",
          }}
        >
          <Grid container sx={v4GridContainerWidthSx}>
            <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={stepWrapperSx}>
              {props.showStepper !== false && (
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={stepTextSx(dark)}
                      style={{ marginBottom: 0 }}
                    >
                      Step <b>2</b> of <b>2</b>{" "}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      style={{ marginBottom: 0 }}
                      sx={stepTextBackSx(dark)}
                      onClick={handleMembershipChange}
                    >
                      &lt; Go Back to Memberships
                    </Typography>
                  </Box>
                </Box>
              )}
              <Typography
                variant="body2"
                sx={responseBodytextSx(dark)}
              ></Typography>
              <Typography variant="h2" sx={stepTitleSx(dark)}>
                Pay with Credit/Debit Card
              </Typography>
              {/* {!paymentPending && ( */}
              <>
                <Box sx={[orphanGridItemFullSx, selectedMembershipContainerSx]}>
                  <Typography
                    sx={selectedMembershipLabelSx(dark)}
                    variant="body1"
                  >
                    Membership
                  </Typography>
                  <Typography sx={selectedMembershipBodyTextSx(dark)}>
                    Check your selected subscription before continuing with
                    payment.
                  </Typography>
                  <Box sx={[{ display: "flex" }, selectedMembershipSx(dark)]}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="body1"
                        sx={selectedMembershipTitleSx(dark)}
                      >
                        {selectedSubscription.name}
                        <br />
                        <SelectedMembershipPriceSpan dark={dark}>
                          {CurrencySymbol({
                            currency: selectedSubscription.currency,
                          })}
                          {selectedSubscription.cost}
                        </SelectedMembershipPriceSpan>
                        {selectedSubscription.isSubscription && (
                          <SelectedMembershipTermSpan dark={dark}>
                            /{selectedSubscription.entitlementPeriodUnit}
                          </SelectedMembershipTermSpan>
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        size="small"
                        sx={changeButtonSx(dark)}
                        onClick={handleMembershipChange}
                      >
                        Change
                      </Button>
                    </Box>
                  </Box>
                </Box>
                <Box sx={orphanGridItemFullSx}>
                  <CardPaymentForm
                    Subscription={selectedSubscription}
                    dark={props.dark}
                    handlePaymentResponse={setPaymentResponse}
                  />
                </Box>
              </>
              {/* )} */}
            </Grid>
          </Grid>
        </Container>
      </>
    );
  }

  useEffect(() => {
    if (search) {
      const { app, product } = querystring.parse(search);
      setQs({
        app: app ? parseInt(app) === 1 : false,
        product: product ? product : undefined,
      });
    }
  }, [search]);

  useEffect(() => {
    if (subscriptionProducts && qs.product && subscriptionProducts.results) {
      const selectedProduct = subscriptionProducts.results.find(
        (product) =>
          product.id === qs.product &&
          (!membershipDetails ||
            (membershipDetails.currency &&
              membershipDetails.currency.toLowerCase() ===
                product.currency.toLowerCase())),
      );

      if (selectedProduct) {
        onHandleSubscription(selectedProduct);
        setActiveStep(1);
      } else {
        setSelectedSubscription("");
        setSelectedSubscriptionID("");
        setActiveStep(0);
      }
    }
  }, [qs, subscriptionProducts, membershipDetails]);

  return (
    <>
      <BackgroundDiv dark={dark}>
        <Container>
          {loading ? (
            <Box sx={loadingBoxSx}>
              <CircularProgress color="primary" />
              <Typography variant="body1" sx={loadingTextSx(dark)}>
                Loading Memberships...
              </Typography>
            </Box>
          ) : (
            <Grid container sx={v4SpacingContainerSx}>
              <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={v4GridItemSx}>
                {getStepContent(activeStep)}
              </Grid>
            </Grid>
          )}
        </Container>
      </BackgroundDiv>
    </>
  );
};

export default Steps;
